import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { DatePicker } from "@/components/date-picker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AddressFields } from "@/features/canteiros/components/address-fields";
import { useCreateCanteiro } from "@/features/canteiros/hooks/use-create-canteiro.ts";
import {
	type CanteiroFormValues,
	canteiroSchema,
} from "@/features/canteiros/schemas/canteiro-schema.ts";
import { toCanteiro } from "@/features/canteiros/utils.ts";
import { dateOnlyToDate, dateToDateOnly, dateToRFC3339 } from "@/lib/format.ts";
import { useActiveContext } from "@/store/use-active-context.ts";

const VIOLATION_FIELDS: Record<string, keyof CanteiroFormValues> = {
	name: "name",
	"address.street": "street",
	"address.number": "number",
	"address.complement": "complement",
	"address.neighborhood": "neighborhood",
	"address.city": "city",
	"address.state": "state",
	"address.zipcode": "zipcode",
	area_sqm: "area",
	start_date: "startDate",
	expected_end_date: "expectedEndDate",
};

export function NovoCanteiroPage() {
	const navigate = useNavigate();
	const setActiveCanteiro = useActiveContext((state) => state.setActiveCanteiro);
	const createWorksite = useCreateCanteiro();
	const [formError, setFormError] = useState<string | null>(null);

	const {
		register,
		control,
		handleSubmit,
		setError,
		setValue,
		watch,
		formState: { errors },
	} = useForm<CanteiroFormValues>({
		resolver: zodResolver(canteiroSchema),
		defaultValues: {
			name: "",
			street: "",
			number: "",
			complement: "",
			neighborhood: "",
			city: "",
			state: "",
			zipcode: "",
			lat: undefined,
			lng: undefined,
			area: "",
			startDate: "",
			expectedEndDate: "",
		},
	});

	function applyAPIError(err: unknown, fallback: string) {
		if (err instanceof APIError) {
			let matchedField = false;
			for (const [field, message] of Object.entries(err.violations)) {
				const formField = VIOLATION_FIELDS[field];
				if (formField) {
					setError(formField, { message });
					matchedField = true;
				}
			}
			if (!matchedField) setFormError(err.message || fallback);
			return;
		}
		setFormError(err instanceof Error ? err.message : fallback);
	}

	async function onSubmit(values: CanteiroFormValues) {
		setFormError(null);
		const area = values.area?.trim();
		const areaSqm = area ? Number(area.replace(",", ".")) : undefined;

		try {
			const worksite = await createWorksite.mutateAsync({
				name: values.name,
				address: {
					street: values.street,
					number: values.number,
					complement: values.complement?.trim() || undefined,
					neighborhood: values.neighborhood,
					city: values.city,
					state: values.state.toUpperCase(),
					zipcode: values.zipcode,
					lat: values.lat,
					lng: values.lng,
				},
				areaSqm: Number.isFinite(areaSqm) ? areaSqm : undefined,
				startDate: dateToRFC3339(values.startDate),
				expectedEndDate: dateToRFC3339(values.expectedEndDate),
			});
			const canteiro = toCanteiro(worksite);
			setActiveCanteiro(canteiro.id);
			toast.success("Canteiro criado com sucesso!");
			navigate("/", { replace: true });
		} catch (err) {
			applyAPIError(err, "Não foi possível criar o canteiro.");
		}
	}

	const isPending = createWorksite.isPending;

	return (
		<div className="novo-canteiro-page">
			<div className="novo-canteiro-page__container">
				<header className="novo-canteiro-page__header">
					<button
						type="button"
						className="novo-canteiro-page__back"
						onClick={() => navigate(-1)}
					>
						<ArrowLeft />
						Voltar
					</button>
					<h1 className="novo-canteiro-page__title">Novo canteiro</h1>
					<p className="novo-canteiro-page__subtitle">
						Cadastre um canteiro para começar a acompanhar suas obras.
					</p>
				</header>

				<form
					className="novo-canteiro-page__form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<div className="form-group">
						<Label htmlFor="canteiro-name">Nome do canteiro</Label>
						<Input
							id="canteiro-name"
							placeholder="Ex.: Residencial Lagoa Nova"
							aria-invalid={!!errors.name}
							{...register("name")}
						/>
						{errors.name && <p className="form-error">{errors.name.message}</p>}
					</div>

					<AddressFields
						register={register}
						errors={errors}
						setValue={setValue}
						watch={watch}
						idPrefix="canteiro"
					/>

					<div className="novo-canteiro-page__section">
						<p className="novo-canteiro-page__section-title">Detalhes (opcional)</p>
						<div className="form-group">
							<Label htmlFor="canteiro-area">Área (m²)</Label>
							<Input
								id="canteiro-area"
								inputMode="decimal"
								placeholder="4200"
								{...register("area")}
							/>
						</div>
						<div className="novo-canteiro-page__row">
							<div className="form-group">
								<Label htmlFor="canteiro-start">Início previsto</Label>
								<Controller
									control={control}
									name="startDate"
									render={({ field }) => (
										<DatePicker
											id="canteiro-start"
											name={field.name}
											selected={dateOnlyToDate(field.value)}
											onSelected={(date) =>
												field.onChange(dateToDateOnly(date))
											}
											aria-invalid={!!errors.startDate}
										/>
									)}
								/>
							</div>
							<div className="form-group">
								<Label htmlFor="canteiro-end">Entrega prevista</Label>
								<Controller
									control={control}
									name="expectedEndDate"
									render={({ field }) => (
										<DatePicker
											id="canteiro-end"
											name={field.name}
											selected={dateOnlyToDate(field.value)}
											onSelected={(date) =>
												field.onChange(dateToDateOnly(date))
											}
											aria-invalid={!!errors.expectedEndDate}
										/>
									)}
								/>
							</div>
						</div>
					</div>

					{formError && (
						<p role="alert" className="form-error-alert">
							{formError}
						</p>
					)}

					<div className="novo-canteiro-page__actions">
						<Button
							type="button"
							variant="ghost"
							onClick={() => navigate(-1)}
							disabled={isPending}
						>
							Cancelar
						</Button>
						<Button type="submit" disabled={isPending}>
							{isPending && <Loader2 className="novo-canteiro-page__spinner" />}
							Criar canteiro
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}
